import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useNavigate, useParams } from "react-router-dom";

import {
  HiOutlineArrowLeft,
  HiOutlineFolder,
  HiOutlineUser,
  HiOutlineDocumentText,
  HiOutlineCalendarDays,
  HiOutlineClipboardDocumentList,
} from "react-icons/hi2";

import PageHeader from "../../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../../components/ui/pageEmpty/PageEmpty";

import DossierDynamicForm from "../../../../components/dossierForm/DossierDynamicForm";

import { getDossierById } from "../../../../redux/features/dossiers/dossierSlice";

import { getFieldConfigs } from "../../../../redux/features/fieldConfigs/fieldConfigSlice";

import "./DossierDetailsPage.css";

/**
 * =====================================================
 * STEP LABELS
 * =====================================================
 */

const STEP_LABELS = {
  aanmelding: "Aanmelding",

  administratie: "Administratie",

  begeleiding: "Begeleiding",

  vdab: "VDAB",

  kld: "KLD",

  afronding: "Afronding",
};

export default function DossierDetailsPage() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { id } = useParams();

  /**
   * =====================================================
   * REDUX
   * =====================================================
   */

  const { selectedDossier, isLoading, error } = useSelector(
    (state) => state.dossiers,
  );

  const {
    fieldConfigs,
    isLoading: isLoadingFieldConfigs,
    error: fieldConfigError,
  } = useSelector((state) => state.fieldConfigs);

  /**
   * =====================================================
   * LOAD DOSSIER
   * =====================================================
   */

  useEffect(() => {
    if (!id) return;

    dispatch(getDossierById(id));
  }, [dispatch, id]);

  /**
   * =====================================================
   * LOAD FIELD CONFIGS
   * =====================================================
   */

  const schoolYearId =
    selectedDossier?.school_year?._id || selectedDossier?.school_year;

  useEffect(() => {
    if (!schoolYearId) return;

    dispatch(
      getFieldConfigs({
        schoolYearId,
        isActive: true,
      }),
    );
  }, [dispatch, schoolYearId]);

  /**
   * =====================================================
   * LOADING
   * =====================================================
   */

  if (isLoading || isLoadingFieldConfigs) {
    return <PageLoader title="Dossier laden..." />;
  }

  /**
   * =====================================================
   * NOT FOUND
   * =====================================================
   */

  if (!selectedDossier) {
    return (
      <div className="dossier-details-page">
        <PageEmpty
          icon={<HiOutlineFolder />}
          title="Dossier niet gevonden"
          description={
            error || "Dit dossier bestaat niet of je hebt geen toegang."
          }
        />
      </div>
    );
  }

  /**
   * =====================================================
   * CLIENT NAME
   * =====================================================
   */

  const clientName =
    selectedDossier?.client?.display_name ||
    `${selectedDossier?.client?.first_name || ""} ${
      selectedDossier?.client?.last_name || ""
    }`.trim() ||
    "Onbekende cliënt";

  /**
   * =====================================================
   * CURRENT STEP
   * =====================================================
   */

  const currentStep =
    STEP_LABELS[selectedDossier.current_step] ||
    selectedDossier.current_step ||
    "-";

  return (
    <div className="dossier-details-page">
      {/* =====================================================
          HEADER
      ===================================================== */}
      <PageHeader
        eyebrow="POWER"
        title={clientName}
        description="Dynamisch POWER dossier."
        actions={
          <button
            className="dossier-details-page__back-button"
            onClick={() => navigate("/dashboard/dossiers")}
          >
            <HiOutlineArrowLeft />

            <span>Terug</span>
          </button>
        }
      />

      {/* =====================================================
          ERROR
      ===================================================== */}
      {(error || fieldConfigError) && (
        <div className="dossier-details-page__error">
          {error || fieldConfigError}
        </div>
      )}

      {/* =====================================================
          SUMMARY
      ===================================================== */}
      <div className="dossier-details-page__summary-grid">
        {/* DOSSIER */}
        <PageCard className="dossier-details-page__summary-card">
          <div className="dossier-details-page__summary-icon">
            <HiOutlineDocumentText />
          </div>

          <div className="dossier-details-page__summary-content">
            <span>Dossiernummer</span>

            <strong>
              #
              {selectedDossier.sequence_number ||
                selectedDossier.dossier_number ||
                selectedDossier._id}
            </strong>
          </div>
        </PageCard>

        {/* CLIENT */}
        <PageCard className="dossier-details-page__summary-card">
          <div className="dossier-details-page__summary-icon">
            <HiOutlineUser />
          </div>

          <div className="dossier-details-page__summary-content">
            <span>Cliënt</span>

            <strong>{clientName}</strong>
          </div>
        </PageCard>

        {/* STEP */}
        <PageCard className="dossier-details-page__summary-card">
          <div className="dossier-details-page__summary-icon">
            <HiOutlineClipboardDocumentList />
          </div>

          <div className="dossier-details-page__summary-content">
            <span>Huidige stap</span>

            <strong>{currentStep}</strong>
          </div>
        </PageCard>

        {/* SCHOOL YEAR */}
        <PageCard className="dossier-details-page__summary-card">
          <div className="dossier-details-page__summary-icon">
            <HiOutlineCalendarDays />
          </div>

          <div className="dossier-details-page__summary-content">
            <span>Schooljaar</span>

            <strong>{selectedDossier.school_year?.name || "-"}</strong>
          </div>
        </PageCard>
      </div>

      {/* =====================================================
          STATUS BAR
      ===================================================== */}
      <PageCard className="dossier-details-page__status-card">
        <div className="dossier-details-page__status-grid">
          <div>
            <span>Status</span>

            <strong>{selectedDossier.status || "-"}</strong>
          </div>

          <div>
            <span>Coach</span>

            <strong>
              {selectedDossier.coach
                ? `${selectedDossier.coach.first_name} ${selectedDossier.coach.last_name}`
                : "-"}
            </strong>
          </div>

          <div>
            <span>Archiefstatus</span>

            <strong>
              {selectedDossier.is_archived ? "Gearchiveerd" : "Actief"}
            </strong>
          </div>

          <div>
            <span>Aangemaakt</span>

            <strong>
              {selectedDossier.createdAt
                ? new Date(selectedDossier.createdAt).toLocaleDateString(
                    "nl-BE",
                  )
                : "-"}
            </strong>
          </div>
        </div>
      </PageCard>

      {/* =====================================================
          NO FIELD CONFIGS
      ===================================================== */}
      {!fieldConfigs || fieldConfigs.length === 0 ? (
        <PageEmpty
          icon={<HiOutlineFolder />}
          title="Geen actieve velden gevonden"
          description="Er zijn geen actieve veldconfiguraties voor dit schooljaar."
        />
      ) : (
        /**
         * =====================================================
         * DYNAMIC DOSSIER FORM
         * =====================================================
         */
        <DossierDynamicForm dossier={selectedDossier} fields={fieldConfigs} />
      )}
    </div>
  );
}
